import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TablePagination,
  TableSortLabel,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Cancel, Search, Tune as TuneIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Linkify from 'react-linkify';
import { WaveLoading } from '../components/Loader';
import ToolbarButton from '../components/ToolbarButton';
import StatusPill from '../components/StatusPill';

const QCReview = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('submitted_for_review');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      fetchQCReviewTasks();
    }
  }, [user]);

  const fetchQCReviewTasks = async () => {
    try {
      setLoading(true);
      
      // Fetch all tasks and filter for submitted_for_review status
      const tasksResponse = await fetch(`${process.env.REACT_APP_API_URL}/tasks?page=1&limit=10000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!tasksResponse.ok) throw new Error('Failed to fetch tasks');
      
      const tasksData = await tasksResponse.json();
      const allTasks = Array.isArray(tasksData) ? tasksData : tasksData.data || [];
      
      // Filter tasks with status: submitted_for_review, completed, or rejected
      const qcTasks = allTasks.filter(task => 
        task.status === 'submitted_for_review' || 
        task.status === 'completed' || 
        task.status === 'rejected'
      );
      
      // Fetch all projects to get manager and teamMembers
      const projectsResponse = await fetch(`${process.env.REACT_APP_API_URL}/projects?page=1&limit=10000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!projectsResponse.ok) throw new Error('Failed to fetch projects');
      
      const projectsData = await projectsResponse.json();
      const allProjects = Array.isArray(projectsData) ? projectsData : projectsData.data || [];
      setProjects(allProjects);
      
      // Merge project details (manager, teamMembers) with tasks
      const tasksWithProjectDetails = qcTasks.map(task => {
        const project = allProjects.find(p => p.id === task.projectId || p.id === task.Project?.id);
        if (project) {
          return {
            ...task,
            Project: {
              ...task.Project,
              manager: project.manager,
              teamMembers: project.teamMembers,
            }
          };
        }
        return task;
      });
      
      setTasks(tasksWithProjectDetails);
    } catch (error) {
      console.error('Error fetching QC review tasks:', error);
      setError('Failed to fetch QC review tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (task) => {
    setSelectedTask(task);
    setApproveDialogOpen(true);
  };

  const handleReject = (task) => {
    setSelectedTask(task);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedTask || isProcessing) return;

    setIsProcessing(true);
    try {
      // Update task status to completed
      const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/tasks/${selectedTask.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          status: 'completed',
        }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({ message: 'Failed to update task status' }));
        throw new Error(errorData.message || 'Failed to update task status');
      }

      // Create QC audit log entry
      let auditLogSuccess = false;
      try {
        const auditLogData = {
          projectid: selectedTask.projectId || selectedTask.Project?.id,
          Taskid: selectedTask.id,
          Actions: 'Approved',
          reason: null,
          ActionBy: user.name || user.email,
        };

        console.log('[QC Review] Creating audit log for approve:', auditLogData);

        const auditLogResponse = await fetch(`${process.env.REACT_APP_API_URL}/qc-audit-log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(auditLogData),
        });

        if (!auditLogResponse.ok) {
          const errorData = await auditLogResponse.json().catch(() => ({ message: 'Unknown error' }));
          console.error('[QC Review] Failed to create audit log:', errorData);
          console.error('[QC Review] Response status:', auditLogResponse.status);
          console.error('[QC Review] Response statusText:', auditLogResponse.statusText);
          throw new Error(`Audit log creation failed: ${errorData.message || 'Unknown error'}`);
        }

        const auditLogResult = await auditLogResponse.json();
        console.log('[QC Review] Audit log created successfully:', auditLogResult);
        auditLogSuccess = true;
      } catch (auditError) {
        console.error('[QC Review] Error creating audit log:', auditError);
        console.error('[QC Review] Audit error details:', {
          message: auditError.message,
          stack: auditError.stack
        });
        // Continue even if audit log fails - task is already updated
      }

      setApproveDialogOpen(false);
      setSelectedTask(null);
      
      if (auditLogSuccess) {
        setSnackbarMsg('Task approved successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMsg('Task approved, but audit log creation failed. Please check console for details.');
        setSnackbarSeverity('warning');
      }
      setSnackbarOpen(true);
      
      // Refresh the task list
      fetchQCReviewTasks();
    } catch (error) {
      console.error('[QC Review] Error approving task:', error);
      setApproveDialogOpen(false);
      setSelectedTask(null);
      setSnackbarMsg(`Failed to approve task: ${error.message || 'Please try again.'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmReject = async () => {
    if (!selectedTask || isProcessing) return;

    if (!rejectionReason.trim()) {
      setSnackbarMsg('Please provide a reason for rejection.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsProcessing(true);
    try {
      // Update task status to rejected
      const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/tasks/${selectedTask.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          status: 'rejected',
        }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({ message: 'Failed to update task status' }));
        throw new Error(errorData.message || 'Failed to update task status');
      }

      // Create QC audit log entry
      let auditLogSuccess = false;
      try {
        const auditLogData = {
          projectid: selectedTask.projectId || selectedTask.Project?.id,
          Taskid: selectedTask.id,
          Actions: 'Rejected',
          reason: rejectionReason.trim(),
          ActionBy: user.name || user.email,
        };

        console.log('[QC Review] Creating audit log for reject:', auditLogData);

        const auditLogResponse = await fetch(`${process.env.REACT_APP_API_URL}/qc-audit-log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(auditLogData),
        });

        if (!auditLogResponse.ok) {
          const errorData = await auditLogResponse.json().catch(() => ({ message: 'Unknown error' }));
          console.error('[QC Review] Failed to create audit log:', errorData);
          console.error('[QC Review] Response status:', auditLogResponse.status);
          console.error('[QC Review] Response statusText:', auditLogResponse.statusText);
          throw new Error(`Audit log creation failed: ${errorData.message || 'Unknown error'}`);
        }

        const auditLogResult = await auditLogResponse.json();
        console.log('[QC Review] Audit log created successfully:', auditLogResult);
        auditLogSuccess = true;
      } catch (auditError) {
        console.error('[QC Review] Error creating audit log:', auditError);
        console.error('[QC Review] Audit error details:', {
          message: auditError.message,
          stack: auditError.stack
        });
        // Continue even if audit log fails - task is already updated
      }

      setRejectDialogOpen(false);
      setSelectedTask(null);
      setRejectionReason('');
      
      if (auditLogSuccess) {
        setSnackbarMsg('Task rejected successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMsg('Task rejected, but audit log creation failed. Please check console for details.');
        setSnackbarSeverity('warning');
      }
      setSnackbarOpen(true);
      
      // Refresh the task list
      fetchQCReviewTasks();
    } catch (error) {
      console.error('[QC Review] Error rejecting task:', error);
      setRejectDialogOpen(false);
      setSelectedTask(null);
      setSnackbarMsg(`Failed to reject task: ${error.message || 'Please try again.'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseApproveDialog = () => {
    if (isProcessing) return; // Prevent closing during processing
    setApproveDialogOpen(false);
    setSelectedTask(null);
  };

  const handleCloseRejectDialog = () => {
    if (isProcessing) return; // Prevent closing during processing
    setRejectDialogOpen(false);
    setSelectedTask(null);
    setRejectionReason('');
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  function descendingComparator(a, b, prop) {
    if (prop === 'projectName') {
      const aVal = a.Project?.name || '';
      const bVal = b.Project?.name || '';
      if (bVal < aVal) return -1;
      if (bVal > aVal) return 1;
      return 0;
    }
    if (prop === 'projectManager') {
      const aVal = a.Project?.manager?.name || '';
      const bVal = b.Project?.manager?.name || '';
      if (bVal < aVal) return -1;
      if (bVal > aVal) return 1;
      return 0;
    }
    if (prop === 'submittedDate') {
      const aVal = a.submittedDate || '';
      const bVal = b.submittedDate || '';
      if (bVal < aVal) return -1;
      if (bVal > aVal) return 1;
      return 0;
    }
    if (b[prop] < a[prop]) return -1;
    if (b[prop] > a[prop]) return 1;
    return 0;
  }

  function getComparator(ord, prop) {
    return ord === 'desc'
      ? (a, b) => descendingComparator(a, b, prop)
      : (a, b) => -descendingComparator(a, b, prop);
  }

  function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const orderRes = comparator(a[0], b[0]);
      if (orderRes !== 0) return orderRes;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }

  // Get unique project names from tasks for autocomplete
  // Note: Tasks are already filtered by backend teamScope middleware to only include Marcom projects
  // So projectNames will automatically only show Marcom project names
  const projectNames = React.useMemo(() => {
    const uniqueProjects = new Set();
    tasks.forEach(task => {
      const projectName = task.Project?.name;
      if (projectName) {
        uniqueProjects.add(projectName);
      }
    });
    return Array.from(uniqueProjects).sort();
  }, [tasks]);

  // Filter tasks by selected project and status
  const filteredTasks = React.useMemo(() => {
    let filtered = tasks;
    
    // Filter by project name
    if (selectedProjectFilter) {
      filtered = filtered.filter(task => task.Project?.name === selectedProjectFilter);
    }
    
    // Filter by status
    if (selectedStatusFilter && selectedStatusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === selectedStatusFilter);
    }
    
    return filtered;
  }, [tasks, selectedProjectFilter, selectedStatusFilter]);

  const sortedTasks = stableSort(filteredTasks, getComparator(order, orderBy));
  const paginatedTasks = sortedTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Reset page when filter changes
  React.useEffect(() => {
    setPage(0);
  }, [selectedProjectFilter, selectedStatusFilter]);

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>QC Review</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Filter Section — Notion-style soft bar, no "Filters" title */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: 2.5,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          {/* Status — primary filter, always visible */}
          <Autocomplete
            size="small"
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'submitted_for_review', label: 'Submitted for Review' },
              { value: 'completed', label: 'Completed' },
              { value: 'rejected', label: 'Rejected' },
            ]}
            value={selectedStatusFilter === 'all'
              ? { value: 'all', label: 'All Statuses' }
              : selectedStatusFilter === 'submitted_for_review'
              ? { value: 'submitted_for_review', label: 'Submitted for Review' }
              : selectedStatusFilter === 'completed'
              ? { value: 'completed', label: 'Completed' }
              : { value: 'rejected', label: 'Rejected' }
            }
            onChange={(event, newValue) => {
              setSelectedStatusFilter(newValue ? newValue.value : 'submitted_for_review');
            }}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            sx={{ minWidth: 220 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                placeholder="Select status..."
              />
            )}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <ToolbarButton
              variant={showAdvancedFilters ? 'contained' : 'outlined'}
              startIcon={<TuneIcon />}
              onClick={() => setShowAdvancedFilters((v) => !v)}
            >
              More filters
              {selectedProjectFilter && ' •'}
            </ToolbarButton>
            {(selectedProjectFilter || (selectedStatusFilter && selectedStatusFilter !== 'submitted_for_review')) && (
              <ToolbarButton
                variant="outlined"
                onClick={() => {
                  setSelectedProjectFilter(null);
                  setSelectedStatusFilter('submitted_for_review');
                }}
              >
                Clear All
              </ToolbarButton>
            )}
          </Box>
        </Box>

        {(showAdvancedFilters || selectedProjectFilter) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              mt: 1.5,
              pt: 1.5,
              borderTop: (t) => t.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #e5e7eb',
            }}
          >
            <Autocomplete
              size="small"
              options={projectNames}
              value={selectedProjectFilter}
              onChange={(event, newValue) => {
                setSelectedProjectFilter(newValue);
              }}
              sx={{ minWidth: 280 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Name"
                  placeholder="Type to search..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              clearOnEscape
              clearText="Clear"
              noOptionsText="No projects found"
            />
          </Box>
        )}
      </Box>
      
      <TableContainer 
        component={Paper}
        sx={{
          background: theme.palette.mode === 'dark' 
            ? 'rgba(44, 48, 52, 0.6)' 
            : 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.1)' 
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.3)' 
              : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.5)' 
                : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              <TableCell sortDirection={orderBy === 'projectName' ? order : false} sx={{ fontWeight: 'bold' }}>
                <TableSortLabel 
                  active={orderBy === 'projectName'} 
                  direction={orderBy === 'projectName' ? order : 'asc'} 
                  onClick={() => handleSort('projectName')} 
                  sx={{ color: 'white', '&:hover': { color: 'white' } }}
                >
                  Project Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'title' ? order : false} sx={{ fontWeight: 'bold' }}>
                <TableSortLabel 
                  active={orderBy === 'title'} 
                  direction={orderBy === 'title' ? order : 'asc'} 
                  onClick={() => handleSort('title')} 
                  sx={{ color: 'white', '&:hover': { color: 'white' } }}
                >
                  Task Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>File Location</TableCell>
              <TableCell sortDirection={orderBy === 'projectManager' ? order : false} sx={{ fontWeight: 'bold' }}>
                <TableSortLabel 
                  active={orderBy === 'projectManager'} 
                  direction={orderBy === 'projectManager' ? order : 'asc'} 
                  onClick={() => handleSort('projectManager')} 
                  sx={{ color: 'white', '&:hover': { color: 'white' } }}
                >
                  Project Manager
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Team Members</TableCell>
              <TableCell sortDirection={orderBy === 'submittedDate' ? order : false} sx={{ fontWeight: 'bold' }}>
                <TableSortLabel 
                  active={orderBy === 'submittedDate'} 
                  direction={orderBy === 'submittedDate' ? order : 'asc'} 
                  onClick={() => handleSort('submittedDate')} 
                  sx={{ color: 'white', '&:hover': { color: 'white' } }}
                >
                  Submitted Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <WaveLoading theme={theme} size="large" />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      Loading QC review tasks...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedTasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      No tasks found
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      {selectedStatusFilter === 'all' 
                        ? 'No tasks found with the selected filters'
                        : selectedStatusFilter === 'completed'
                        ? 'No completed tasks found'
                        : selectedStatusFilter === 'rejected'
                        ? 'No rejected tasks found'
                        : 'No tasks submitted for review found'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTasks.map((task) => (
                <TableRow
                  key={task.id}
                  hover
                  sx={{
                    borderBottom: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : '1px solid rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                    }
                  }}
                >
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                    fontWeight: 500
                  }}>
                    {task.Project?.name || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400
                  }}>
                    {task.title}
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400,
                    maxWidth: 300,
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                        {decoratedText}
                      </a>
                    )}>
                      {task.description || 'N/A'}
                    </Linkify>
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400,
                    maxWidth: 300,
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                        {decoratedText}
                      </a>
                    )}>
                      {task.filelocation || 'N/A'}
                    </Linkify>
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400
                  }}>
                    {task.Project?.manager?.name || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400
                  }}>
                    {task.Project?.teamMembers && task.Project.teamMembers.length > 0
                      ? task.Project.teamMembers.map(member => member.name).join(', ')
                      : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400
                  }}>
                    {formatDate(task.submittedDate)}
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400
                  }}>
                    <StatusPill status={task.status} />
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                    fontWeight: 400
                  }}>
                    {task.status === 'submitted_for_review' ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Approve">
                          <IconButton
                            onClick={() => handleApprove(task)}
                            size="small"
                            disabled={isProcessing}
                            sx={{
                              color: '#4caf50',
                              '&:hover': {
                                background: 'rgba(76, 175, 80, 0.1)',
                                color: '#388e3c',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease'
                              },
                              '&:disabled': {
                                opacity: 0.5
                              }
                            }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            onClick={() => handleReject(task)}
                            size="small"
                            disabled={isProcessing}
                            sx={{
                              color: '#f44336',
                              '&:hover': {
                                background: 'rgba(244, 67, 54, 0.1)',
                                color: '#d32f2f',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease'
                              },
                              '&:disabled': {
                                opacity: 0.5
                              }
                            }}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredTasks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog}>
        <DialogTitle>Approve Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to approve this task?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog} disabled={isProcessing}>Cancel</Button>
          <Button 
            onClick={confirmApprove} 
            variant="contained" 
            color="success" 
            autoFocus
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isProcessing ? 'Processing...' : 'Yes, Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Task</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejecting this task. The task status will be updated to "Rejected".
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            fullWidth
            required
            multiline
            rows={4}
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            error={!rejectionReason.trim()}
            helperText={!rejectionReason.trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} disabled={isProcessing}>Cancel</Button>
          <Button 
            onClick={confirmReject} 
            variant="contained" 
            color="error" 
            disabled={!rejectionReason.trim() || isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isProcessing ? 'Processing...' : 'Reject Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QCReview;

